import CityInput from '@/components/FormItems/CityInput';
import { t } from 'i18next';
import CountryInput from '@/components/FormItems/CountryInput';
import AppDatePicker from '@/components/FormItems/DatePicker';
import RegionInput from '@/components/FormItems/RegionInput';
import { ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { AdvertisingDetailType, VideoPlayerType, AdvertisingClickActionType, BlobStorageContainerType } from '@/constants';
import { Button, Card, Col, FormInstance, FormListFieldData, Input, InputNumber, Row, Select, Form } from 'antd';
import React from 'react';
import { FlagOutlined, PlusCircleOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import FormItem from 'antd/es/form/FormItem';
import CompanyInput from '@/components/FormItems/CompanyInput';
import { apiSdk } from '@/httpClient';
import AdvertisingBaseTripInput from '@/components/FormItems/AdvertisingBaseTripInput';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import SingleVideoUpload from '@/components/FormItems/SingleVideoUpload';

type Props = {
  field: FormListFieldData;
  form: FormInstance;
  remove: (key: any) => void;
  canDelete: boolean;
};

const rules = [{ required: true, message: VALIDATE_REQUIRED }];
export default function AdvertisingDetailItem({ field, form, remove, canDelete }: Props) {
  const advertisingDetailType = Form.useWatch(['advertisingDetails', field.name, 'advertisingDetailType'], form);
  const clickAction = Form.useWatch(['advertisingDetails', field.name, 'clickAction'], form);
  const countryId = Form.useWatch(['advertisingDetails', field.name, 'countryId'], form);
  const regionId = Form.useWatch(['advertisingDetails', field.name, 'regionId'], form);
  const tripCompanyId = Form.useWatch(['advertisingDetails', field.name, 'tripCompanyId'], form);
  const videoPlayerType = Form.useWatch(['advertisingDetails', field.name, 'videoPlayerType'], form);

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 10 }}>{t('Order')}</span>
          <FormItem className="no-margin" name={[field.name, 'order']} rules={rules}>
            <InputNumber />
          </FormItem>
        </div>
      }
      size="small"
      headStyle={{ background: '#ccc' }}
      extra={<Button disabled={!canDelete} onClick={() => remove(field.key)} icon={<DeleteOutlined />} type="primary" danger size="small"></Button>}
    >
      <Row gutter={ROW_GUTTER}>
        <Col md={4}>
          <FormItem label={t('Start Date')} name={[field.name, 'startDate']} rules={rules}>
            <AppDatePicker />
          </FormItem>
        </Col>
        <Col md={4}>
          <FormItem label={t('End Date')} name={[field.name, 'endDate']} rules={rules}>
            <AppDatePicker />
          </FormItem>
        </Col>
        <Col md={16}>
          <Row gutter={ROW_GUTTER}>
            <Col md={8}>
              <FormItem label={t('Country')} name={[field.name, 'countryId']} rules={rules}>
                <CountryInput
                  allowClear
                  onChange={() => {
                    form.setFieldValue(['advertisingDetails', field.name, 'regionId'], null);
                  }}
                />
              </FormItem>
            </Col>

            <Col md={8}>
              <FormItem label={t('Region')} name={[field.name, 'regionId']} rules={rules}>
                <RegionInput
                  disabled={!countryId}
                  allowClear
                  countryId={countryId}
                  onChange={() => {
                    form.setFieldValue(['advertisingDetails', field.name, 'ciyId'], null);
                  }}
                />
              </FormItem>
            </Col>
            <Col md={8}>
              <FormItem label={t('City')} name={[field.name, 'cityId']} rules={rules}>
                <CityInput allowClear regionId={regionId} disabled={!regionId} />
              </FormItem>
            </Col>
          </Row>
        </Col>

        <Col md={4}>
          <FormItem label={t('Type')} name={[field.name, 'advertisingDetailType']} rules={rules}>
            <Select options={AdvertisingDetailType.__getSelectOptions()} />
          </FormItem>
        </Col>
        {advertisingDetailType === AdvertisingDetailType.Image ? (
          <Col md={20}>
            <FormItem label={t('Image URL')} name={[field.name, 'contentUrl']} rules={rules}>
              <SingleImageUpload blobStorageContainer={BlobStorageContainerType.TransportPassengers} multiple={false} />
            </FormItem>
          </Col>
        ) : (
          <>
            <Col md={4}>
              <FormItem label={t('Type')} name={[field.name, 'videoPlayerType']} rules={rules}>
                <Select options={VideoPlayerType.__getSelectOptions()} />
              </FormItem>
            </Col>
            <Col md={8}>
              <FormItem label={t('Video Thumbnail Url')} name={[field.name, 'videoThumbnailUrl']} rules={rules}>
                <SingleImageUpload blobStorageContainer={BlobStorageContainerType.TransportPassengers} multiple={false} />
              </FormItem>
            </Col>
            <Col md={8}>
              {videoPlayerType === VideoPlayerType.Youtube ? (
                <FormItem label={t('Video Url')} name={[field.name, 'contentUrl']} rules={rules}>
                  <Input />
                </FormItem>
              ) : (
                <FormItem label={t('Upload video')} name={[field.name, 'videoId']} rules={rules}>
                  <SingleVideoUpload />
                </FormItem>
              )}
            </Col>
          </>
        )}

        <Col md={4}>
          <FormItem label={t('Click Action')} name={[field.name, 'clickAction']} rules={rules}>
            <Select options={AdvertisingClickActionType.__getSelectOptions()} />
          </FormItem>
        </Col>
        {clickAction === AdvertisingClickActionType.ExternalUrl ? (
          <Col md={20}>
            <FormItem label={t('External Url')} name={[field.name, 'externalUrl']} rules={rules}>
              <Input />
            </FormItem>
          </Col>
        ) : clickAction === AdvertisingClickActionType.LinkToTripDetail ? (
          <>
            <Col md={10}>
              <FormItem label={t('tripCompanyId')} name={[field.name, 'tripCompanyId']} rules={rules}>
                <CompanyInput
                  onChange={() => {
                    form.setFieldValue(['advertisingDetails', field.name, 'baseTripId'], null);
                  }}
                />
              </FormItem>
            </Col>
            <Col md={10}>
              <FormItem label={t('Base Trip')} name={[field.name, 'baseTripId']} rules={rules}>
                <AdvertisingBaseTripInput companyId={tripCompanyId} disabled={!tripCompanyId} />
              </FormItem>
            </Col>
          </>
        ) : null}
      </Row>
    </Card>
  );
}
